import { Clock } from "lucide-react";
import { useState } from "react";
import CycleTimeChevronStrip, {
	buildPhasesFromSummary,
	CycleTimeChevronStripSkeleton,
	formatPhaseDuration,
} from "./CycleTimeChevronStrip";
import {
	type MetricsCardFetchScope,
	useCycleTimeQuery,
} from "./metricsCardQueries";

function hasStripData(summary: Record<string, unknown> | null | undefined) {
	if (!summary) return false;
	const merged =
		(summary.mergedCount as number | undefined) ??
		(summary.prCount as number | undefined) ??
		(summary.prCountToQa as number | undefined) ??
		0;
	if (merged > 0) return true;
	return buildPhasesFromSummary(summary).some((phase) => phase.minutes != null);
}

type CycleTimeSectionProps = {
	fetchScope?: MetricsCardFetchScope;
	cycleTimeConfig?: unknown;
	className?: string;
};

export default function CycleTimeSection({
	fetchScope,
	cycleTimeConfig,
	className = "",
}: CycleTimeSectionProps) {
	const [windowDays, setWindowDays] = useState(365);
	const cycleQuery = useCycleTimeQuery(fetchScope, windowDays, {
		cycleTimeConfig,
	});
	const cycleTime = cycleQuery.data?.cycleTime ?? null;
	const loading = cycleQuery.isPending;
	const summary = cycleTime?.summary;
	const phases = summary ? buildPhasesFromSummary(summary) : [];
	const showStrip = hasStripData(summary);
	const totalMinutes = showStrip
		? phases.reduce((sum, p) => sum + (p.minutes ?? 0), 0)
		: 0;

	const windowSelect = fetchScope?.orgId ? (
		<>
			<label htmlFor="cycle-time-window-select" className="sr-only">
				Cycle time window (days)
			</label>
			<select
				id="cycle-time-window-select"
				value={String(windowDays)}
				onChange={(e) => setWindowDays(Number(e.target.value))}
				className="select-mdl py-2 min-w-[140px] text-sm"
				aria-label="Cycle time window (days)"
			>
				<option value="7">7 days</option>
				<option value="30">30 days</option>
				<option value="365">365 days</option>
			</select>
		</>
	) : null;

	const cardHeader = (
		<div className="flex flex-nowrap items-center gap-3 px-6 pt-6 pb-3 w-full">
			<div className="flex min-w-0 flex-1 items-center gap-2">
				<Clock className="w-5 h-5 shrink-0 text-vivid-red" aria-hidden />
				<h3 className="truncate text-lg font-medium text-slate-100">
					Cycle time
				</h3>
				{showStrip && totalMinutes > 0 ? (
					<span className="text-xs font-medium text-slate-300 bg-dark-elevated border border-dark-border rounded-full px-2.5 py-1 shrink-0">
						Avg {formatPhaseDuration(totalMinutes)} total
					</span>
				) : null}
			</div>
			{windowSelect ? (
				<div className="ml-auto shrink-0">{windowSelect}</div>
			) : null}
		</div>
	);

	const rootClass = ["mt-10", className].filter(Boolean).join(" ");

	if (loading) {
		return (
			<div className={rootClass}>
				<div className="card-mdl-static overflow-hidden">
					{cardHeader}
					<CycleTimeChevronStripSkeleton segmentCount={4} embedded />
				</div>
			</div>
		);
	}

	const emptyMessage =
		"No cycle time phase data in this window. Sync repos and merge PRs on configured branches.";

	return (
		<div className={rootClass}>
			<div className="card-mdl-static overflow-hidden">
				{cardHeader}

				{showStrip ? (
					<CycleTimeChevronStrip phases={phases} embedded />
				) : (
					<div className="px-6 pb-6">
						<p className="text-sm text-slate-400 text-center py-8 border border-dashed border-dark-border rounded-lg">
							{emptyMessage}
						</p>
					</div>
				)}
			</div>
		</div>
	);
}

export type { MetricsCardFetchScope as CycleTimeFetchScope };
